import { useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PING_EVERY_MS = 30 * 60 * 1000;
const LS_KEY = "st.last_loc_ping";

/** Global 30-minute GPS heartbeat.
 * Mounted once in AppShell so pings fire from ANY page of the app (not just
 * the Clock In dashboard). Also fires a catch-up ping whenever the app
 * returns to the foreground (phones suspend JS timers while locked), and an
 * immediate ping when a session starts (via the "st:ping-now" event).
 * The last-ping timestamp is shared through localStorage so multiple tabs /
 * page navigations don't double-ping. */
export function LocationPingHeartbeat() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let stopped = false;

    const post = (payload) => api.post("/duty/location-ping", payload).catch(() => {});

    const sendPing = async () => {
      if (!navigator.geolocation) {
        return post({ client_latitude: null, client_longitude: null, geofence_bypass_reason: "unsupported" });
      }
      try {
        const pos = await new Promise((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false, timeout: 15000, maximumAge: 60000,
          })
        );
        return post({ client_latitude: pos.coords.latitude, client_longitude: pos.coords.longitude });
      } catch (err) {
        const reason = err && err.code === 1 ? "denied" : "unavailable";
        return post({ client_latitude: null, client_longitude: null, geofence_bypass_reason: reason });
      }
    };

    const tick = async (force = false) => {
      const last = Number(localStorage.getItem(LS_KEY) || 0);
      if (!force && Date.now() - last < PING_EVERY_MS) return;
      // Only geolocate when the user actually has an open session.
      try {
        const [a, d] = await Promise.all([api.get("/clock/active"), api.get("/duty/current")]);
        if (stopped) return;
        if (!a.data && !d.data?.active) return;
      } catch {
        return; // offline / auth hiccup — retry next tick
      }
      localStorage.setItem(LS_KEY, String(Date.now()));
      await sendPing();
    };

    tick();
    const id = setInterval(() => tick(), 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const onPingNow = () => tick(true);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("st:ping-now", onPingNow);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("st:ping-now", onPingNow);
    };
  }, [user]);

  return null;
}
